import * as React from "react";
import { Eye, FileText, LayoutList, MoreHorizontal, Network, Trash2, UserPlus } from "lucide-react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useCreateBusinessReportFromSnapshotCollectionMutation,
  useGetBusinessReportsQuery,
} from "@/modules/snapshots/businessReports.api";
import { BusinessReport, VariablesDict } from "@/modules/snapshots/businessReports.types";
import { InjectVariablesModal } from "@/modules/snapshots/modals/injectVariablesModal";
import { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import type { SnapshotView } from "@/modules/snapshots/snapshotViews.types";
import { useDeleteSnapshotViewMutation, useUpdateSnapshotViewMutation } from "@/modules/snapshots/snapshotViews.api";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useCanSummariseSupplierCollection } from "@/modules/snapshots/grid/useCanSummariseSupplierCollection";
import {
  SUPPLIER_SUMMARY_COLLECTION_LABEL,
  SupplierSummaryCollectionDialog,
} from "@/modules/snapshots/grid/supplierSummaryCollectionDialog";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Props {
  snapshotCollection: SnapshotCollection;
  onShare: () => void;
  normalViewPath: string;
  onSaveView: () => void;
  snapshotView?: SnapshotView;
  /** The template columns "Create Supplier Summary Collection" offers to regenerate. */
  summaryColumns?: BusinessReport["prompts"];
}

export const SnapshotGridActionsMenu: React.FC<Props> = ({
  snapshotCollection,
  onShare,
  normalViewPath,
  onSaveView,
  snapshotView,
  summaryColumns,
}) => {
  const navigate = useNavigate();
  const [showSupplierSummaryDialog, setShowSupplierSummaryDialog] = React.useState(false);
  const canCreateSupplierSummaryCollection = useCanSummariseSupplierCollection(snapshotCollection);
  const { data: businessReportTemplates = [] } = useGetBusinessReportsQuery({ type: "business_report" });
  const [createBusinessReportFromSnapshotCollection, { isLoading }] =
    useCreateBusinessReportFromSnapshotCollectionMutation();
  const [selectedTemplate, setSelectedTemplate] = React.useState<BusinessReport | null>(null);
  const [showDeleteViewDialog, setShowDeleteViewDialog] = React.useState(false);
  const [updateSnapshotView, updateState] = useUpdateSnapshotViewMutation();
  const [deleteSnapshotView, deleteState] = useDeleteSnapshotViewMutation();
  const user = useAuthenticatedUser();
  const isViewOwner = snapshotView?.owner_id === user.id;

  const toggleViewAccess = async () => {
    if (!snapshotView) return;
    try {
      await updateSnapshotView({
        id: snapshotView.id,
        revision: snapshotView.revision,
        access: snapshotView.access === "public" ? "restricted" : "public",
      }).unwrap();
      toast.success(snapshotView.access === "public" ? "View made private" : "View shared");
    } catch {
      toast.error("Unable to update view access");
    }
  };

  const handleDeleteView = async () => {
    if (!snapshotView) return;
    try {
      await deleteSnapshotView(snapshotView.id).unwrap();
      toast.success("View deleted");
      navigate("/snapshots/views");
    } catch {
      toast.error("Unable to delete view");
    }
  };

  const generateReport = async (template: BusinessReport, variables: VariablesDict = {}) => {
    try {
      const result = await createBusinessReportFromSnapshotCollection({
        snapshot_collection_id: snapshotCollection.id,
        business_report_id: template.id,
        name: `${template.name} - ${snapshotCollection.name}`,
        variables,
        business_report_prompts: template.prompts.map(prompt => ({
          order: prompt.order,
          business_report_prompt_id: prompt.id,
          prompt: prompt.prompt,
          full_context: prompt.full_context,
          name: prompt.name,
          is_date: prompt.is_date,
          data_type: prompt.data_type ?? (prompt.is_date ? "date" : null),
          use_image: prompt.use_image,
          no_retriever: prompt.no_retriever,
          web_search: prompt.web_search,
          traffic_light: prompt.traffic_light,
        })),
      }).unwrap();

      toast.success("Business report generation started");
      navigate(`/reports/outcome/${result.business_report_outcome_id}`);
    } catch {
      toast.error("Unable to generate business report");
    }
  };

  const handleTemplateSelect = (template: BusinessReport) => {
    if (template.variables.length) {
      setSelectedTemplate(template);
      return;
    }
    generateReport(template);
  };

  return (
    <>
      {selectedTemplate && (
        <InjectVariablesModal
          businessReport={selectedTemplate}
          onVariablesModalClose={() => setSelectedTemplate(null)}
          generateReport={variables => {
            generateReport(selectedTemplate, variables);
            setSelectedTemplate(null);
          }}
        />
      )}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Snapshot collection actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onSelect={() => navigate(normalViewPath)} className="cursor-pointer gap-2">
            <LayoutList className="h-4 w-4" />
            Collection view
          </DropdownMenuItem>

          <DropdownMenuItem onSelect={onSaveView} className="cursor-pointer gap-2">
            <Eye className="h-4 w-4" />
            Save View
          </DropdownMenuItem>

          {snapshotView ? (
            isViewOwner && (
              <>
                <DropdownMenuItem
                  disabled={updateState.isLoading}
                  onSelect={toggleViewAccess}
                  className="cursor-pointer gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  {snapshotView.access === "public" ? "Make View private" : "Share View"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setShowDeleteViewDialog(true)}
                  className="cursor-pointer gap-2 text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete View
                </DropdownMenuItem>
              </>
            )
          ) : (
            <DropdownMenuItem onSelect={onShare} className="cursor-pointer gap-2">
              <UserPlus className="h-4 w-4" />
              Share
            </DropdownMenuItem>
          )}

          {canCreateSupplierSummaryCollection && (
            <DropdownMenuItem onSelect={() => setShowSupplierSummaryDialog(true)} className="cursor-pointer gap-2">
              <Network className="h-4 w-4" />
              {SUPPLIER_SUMMARY_COLLECTION_LABEL}
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              disabled={isLoading || businessReportTemplates.length === 0}
              className="cursor-pointer gap-2"
            >
              <FileText className="h-4 w-4" />
              {isLoading ? "Generating..." : "Generate report"}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-[360px]">
              {businessReportTemplates.map(template => (
                <DropdownMenuItem
                  key={template.id}
                  disabled={isLoading}
                  className="cursor-pointer items-start gap-3 py-3"
                  onSelect={() => handleTemplateSelect(template)}
                >
                  <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900">{template.name}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                      {template.description || "Generate a report using the snapshots in this collection"}
                    </p>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
      {canCreateSupplierSummaryCollection && (
        <SupplierSummaryCollectionDialog
          open={showSupplierSummaryDialog}
          onOpenChange={setShowSupplierSummaryDialog}
          snapshotCollectionId={snapshotCollection.id}
          columns={summaryColumns}
        />
      )}
      <Dialog open={showDeleteViewDialog} onOpenChange={setShowDeleteViewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete View?</DialogTitle>
            <DialogDescription>
              This removes the saved configuration. The source collection and its snapshots are not deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteViewDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={deleteState.isLoading} onClick={handleDeleteView}>
              Delete View
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
